/**
 * Traffic Attribution Utilities
 * Captures and manages traffic source attribution data
 * Used by both SDK and checkout flow
 *
 * Attribution Strategy:
 * - First-touch is immutable until expiry (never rebuilt from a revisit)
 * - Last-touch updates on any revisit carrying a signal: paid click id,
 *   UTM params, or an external referrer (last non-direct touch model)
 * - Ad click ids are sticky: a later non-paid touch never erases them, so a
 *   reservation stays linked to the ad click that earned it
 * - Data expires after 90 days
 */

import {
  isDisplayMedium,
  isEmailMedium,
  isPaidMedium,
  isSearchSource,
  isSocialMedium,
  isSocialSource,
  normalizeUtmKey,
  trimUtmValue,
} from './utmStandard';

export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

export interface TrafficAttribution {
  // UTM Parameters (First-Touch)
  utmParams: UTMParams;

  // Google Ads Click IDs (First-Touch)
  gclid?: string; // Google Click ID
  gbraid?: string; // Google Click ID for iOS 14.5+ (App campaigns)
  wbraid?: string; // Google Click ID for web-to-app conversions

  // Facebook Click IDs (First-Touch)
  fbclid?: string; // Facebook Click ID from URL

  // Microsoft Ads Click ID (First-Touch)
  msclkid?: string; // Microsoft/Bing Ads Click ID

  // Referrer Data (First-Touch)
  referrer: string;
  referrerDomain: string;
  referrerType: string;

  // Landing Page Data (First-Touch)
  landingPage: string;
  landingPagePath: string;
  landingPageQuery: string;

  sessionId: string;
  firstTouchTimestamp: string;

  lastTouchUtmParams?: UTMParams;

  // Last-Touch Ad Platform Click IDs
  lastTouchGclid?: string;
  lastTouchGbraid?: string;
  lastTouchWbraid?: string;
  lastTouchFbclid?: string;
  lastTouchMsclkid?: string;

  lastTouchReferrer?: string;
  lastTouchReferrerDomain?: string;
  lastTouchReferrerType?: string;

  lastTouchLandingPage?: string;
  lastTouchLandingPagePath?: string;

  lastTouchTimestamp?: string;

  deviceType: string;
  browser: string;
  operatingSystem: string;
  screenResolution: string;

  userAgent: string;
}

interface StoredAttributionData {
  attribution: TrafficAttribution;
  expiresAt: number; // Unix timestamp
  version: number; // Schema version for future migrations
}

// Storage keys (industry standard: lowercase with underscores, consistent prefix)
const STORAGE_KEY = 'rvrentpro_traffic_attribution';
const SESSION_KEY = 'rvrentpro_session_id';

// 90 days matches Google Ads' default conversion window: a click must still be
// linkable to the booking it produced. RV rental consideration cycles are long.
const EXPIRY_DAYS = 90;
// v3: records from before the site-wide snippet are discarded on read when
// their first touch is an internal referrer with no signal (see
// isStalePreSnippetRecord).
const SCHEMA_VERSION = 3;

function extractDomain(url: string): string {
  if (!url) return 'direct';
  try {
    const urlObj = new URL(url);
    return urlObj.hostname;
  } catch {
    return 'unknown';
  }
}

/**
 * Referrer lists match on the registrable host or a subdomain of it, never a
 * substring: `x.com` must not catch `wix.com`, `vice` must not catch
 * `rvservice.com`, and `google` must not swallow `mail.google.com` before the
 * email check runs. Country-TLD engines (google.co.uk, yandex.ru,
 * blogspot.de) use patterns.
 */
const EMAIL_HOSTS = [
  'mail.google.com',
  'outlook.live.com',
  'outlook.office.com',
  'outlook.office365.com',
  'mail.yahoo.com',
  'mail.aol.com',
  'mail.proton.me',
  'protonmail.com',
  'mailchimp.com',
  'mailchi.mp',
  'list-manage.com',
  'eepurl.com',
  'campaign-archive.com',
  'sendgrid.net',
  'constantcontact.com',
  'rs6.net',
  'mailerlite.com',
  'klaviyo.com',
  'klclick.com',
  'hubspot.com',
  'hubspotlinks.com',
  'marketo.com',
  'pardot.com',
  'drip.com',
  'convertkit.com',
  'ck.page',
  'activecampaign.com',
  'getresponse.com',
  'aweber.com',
  'sendinblue.com',
  'brevo.com',
];

const SEARCH_ENGINE_HOSTS = [
  'bing.com',
  'duckduckgo.com',
  'baidu.com',
  'ecosia.org',
  'ask.com',
  'search.brave.com',
  'startpage.com',
  'qwant.com',
  'search.aol.com',
];

const SEARCH_ENGINE_PATTERNS = [
  /(^|\.)google\.[a-z]{2,3}(\.[a-z]{2})?$/,
  /(^|\.)yahoo\.[a-z]{2,3}(\.[a-z]{2})?$/,
  /(^|\.)yandex\.[a-z]{2,3}$/,
];

const SOCIAL_HOSTS = [
  'facebook.com',
  'fb.com',
  'fb.me',
  'messenger.com',
  'twitter.com',
  't.co',
  'x.com',
  'instagram.com',
  'linkedin.com',
  'lnkd.in',
  'pinterest.com',
  'pin.it',
  'tiktok.com',
  'snapchat.com',
  'threads.net',
  'nextdoor.com',
];

const VIDEO_HOSTS = [
  'youtube.com',
  'youtu.be',
  'vimeo.com',
  'twitch.tv',
  'dailymotion.com',
  'wistia.com',
  'loom.com',
  'vidyard.com',
];

const COMMUNITY_HOSTS = [
  'reddit.com',
  'quora.com',
  'discord.com',
  'discordapp.com',
  'slack.com',
  'stackoverflow.com',
  'stackexchange.com',
  'github.com',
  'gitlab.com',
  'news.ycombinator.com',
  'producthunt.com',
  'indiehackers.com',
];

const NEWS_HOSTS = [
  'cnn.com',
  'bbc.com',
  'bbc.co.uk',
  'nytimes.com',
  'washingtonpost.com',
  'theguardian.com',
  'reuters.com',
  'apnews.com',
  'foxnews.com',
  'nbcnews.com',
  'cbsnews.com',
  'abcnews.go.com',
  'usatoday.com',
  'huffpost.com',
  'buzzfeed.com',
  'vice.com',
  'vox.com',
  'axios.com',
  'politico.com',
  'thehill.com',
  'bloomberg.com',
  'wsj.com',
  'forbes.com',
  'businessinsider.com',
  'cnbc.com',
  'marketwatch.com',
  'techcrunch.com',
  'theverge.com',
  'wired.com',
  'arstechnica.com',
  'engadget.com',
  'mashable.com',
  'gizmodo.com',
  'cnet.com',
  'zdnet.com',
];

const BLOG_HOSTS = [
  'medium.com',
  'wordpress.com',
  'substack.com',
  'blogger.com',
  'tumblr.com',
  'ghost.io',
  'hashnode.dev',
  'dev.to',
  'beehiiv.com',
  'buttondown.email',
];

const BLOG_PATTERNS = [/(^|\.)blogspot\.[a-z]{2,3}(\.[a-z]{2})?$/];

function hostMatches(
  host: string,
  hosts: string[],
  patterns: RegExp[] = [],
): boolean {
  for (let i = 0; i < hosts.length; i++) {
    if (host === hosts[i] || host.endsWith(`.${hosts[i]}`)) return true;
  }
  for (let i = 0; i < patterns.length; i++) {
    if (patterns[i].test(host)) return true;
  }
  return false;
}

/**
 * Classify referrer type from its hostname. Email before search: Gmail's
 * referrer is `mail.google.com`, and an email click is not an organic search.
 */
export function classifyReferrer(referrer: string): string {
  if (!referrer) return 'direct';

  const domain = extractDomain(referrer).toLowerCase();

  if (hostMatches(domain, EMAIL_HOSTS)) return 'email';
  if (hostMatches(domain, SEARCH_ENGINE_HOSTS, SEARCH_ENGINE_PATTERNS)) {
    return 'search_engine';
  }
  if (hostMatches(domain, SOCIAL_HOSTS)) return 'social_media';
  if (hostMatches(domain, VIDEO_HOSTS)) return 'video';
  if (hostMatches(domain, COMMUNITY_HOSTS)) return 'community';
  if (hostMatches(domain, NEWS_HOSTS)) return 'news';
  if (hostMatches(domain, BLOG_HOSTS, BLOG_PATTERNS)) return 'blog';

  // An internal referrer on a first capture means the landing page had no
  // capture script, so the real source is unknown. The classifiers report it
  // as direct, matching GA, rather than the misleading "Other" it used to be.
  if (typeof window !== 'undefined' && domain === window.location.hostname) {
    return 'internal';
  }

  return 'referral';
}

/**
 * Detect device type from user agent
 */
function detectDeviceType(): string {
  if (typeof navigator === 'undefined') return 'unknown';

  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (
    /Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
      ua,
    )
  ) {
    return 'mobile';
  }
  return 'desktop';
}

/**
 * Detect browser from user agent
 */
function detectBrowser(): string {
  if (typeof navigator === 'undefined') return 'Unknown';

  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  if (ua.includes('Edge')) return 'Edge';
  if (ua.includes('Opera')) return 'Opera';
  return 'Unknown';
}

/**
 * Detect operating system from user agent
 */
function detectOS(): string {
  if (typeof navigator === 'undefined') return 'Unknown';

  const ua = navigator.userAgent;
  if (ua.includes('Win')) return 'Windows';
  if (ua.includes('Mac')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iOS')) return 'iOS';
  return 'Unknown';
}

/**
 * Get or create a unique session ID
 * Sessions are stored in sessionStorage (per-tab)
 */
export function getOrCreateSessionId(): string {
  if (typeof sessionStorage === 'undefined') {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  try {
    let sessionId = sessionStorage.getItem(SESSION_KEY);
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem(SESSION_KEY, sessionId);
    }
    return sessionId;
  } catch {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

function getExpirationTimestamp(): number {
  return Date.now() + EXPIRY_DAYS * 24 * 60 * 60 * 1000;
}

function isExpired(expiresAt: number): boolean {
  return Date.now() > expiresAt;
}

export interface StorageOptions {
  /**
   * Namespace for the stored record. Hosted checkout and booking confirmation
   * share one origin across every merchant, so without a scope the immutable
   * first touch left by merchant A's visitor is attached to merchant B's
   * booking from the same browser. Pass the organization uuid there; omit on
   * the merchant's own site, where the origin already is the scope.
   */
  scope?: string;

  /**
   * Mirror the record into a cookie on the site's registrable domain so
   * `merchant.com`, `www.merchant.com` and `blog.merchant.com` share it.
   * localStorage is per origin, so a visitor who lands on one host and books
   * on another otherwise starts over as Direct.
   *
   * Merchant's own domain only. On our shared checkout host the cookie would
   * be shared across every merchant.
   */
  siteCookie?: boolean;
}

function storageKey(scope?: string): string {
  return scope ? `${STORAGE_KEY}:${scope}` : STORAGE_KEY;
}

/**
 * Before the site-wide snippet, the widget on the booking page was usually
 * the first capture, so the immutable first touch became "internal" with no
 * signal, and the sliding expiry keeps that alive for any returning visitor.
 * Left alone, a merchant who installs the snippet would keep seeing Direct
 * for months. Drop those records; the next capture starts from the real
 * landing.
 */
function isStalePreSnippetRecord(data: StoredAttributionData): boolean {
  if ((data.version ?? 0) >= 3) return false;

  const attribution = data.attribution;
  const hasUtm = Object.values(attribution.utmParams || {}).some(Boolean);

  return (
    attribution.referrerType === 'internal' &&
    !hasUtm &&
    !hasAdClickId(attribution)
  );
}

function readLocal(key: string): StoredAttributionData | null {
  if (typeof localStorage === 'undefined') return null;

  try {
    const stored = localStorage.getItem(key);
    if (!stored) return null;

    const data: StoredAttributionData = JSON.parse(stored);

    if (isExpired(data.expiresAt) || isStalePreSnippetRecord(data)) {
      localStorage.removeItem(key);
      return null;
    }

    return data;
  } catch {
    return null;
  }
}

function writeLocal(key: string, data: StoredAttributionData): void {
  if (typeof localStorage === 'undefined') return;

  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.warn('Failed to store attribution data:', error);
  }
}

/**
 * btoa throws on any non-Latin-1 char (accented campaign names, unicode
 * referrers), which used to silently drop the whole checkout handoff (CO-14).
 * UTF-8-encode first so every payload survives.
 */
function utf8ToBase64(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  // Index loop, not for-of: the SDK build targets es5 and TypedArray
  // iteration there needs downlevelIteration.
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToUtf8(encoded: string): string {
  const binary = atob(encoded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
}

// Cookie values cannot carry `;`, and `+`, `/`, `=` are reserved by some
// hosts' cookie parsers, so the site cookie uses the URL-safe alphabet.
function toBase64Url(text: string): string {
  return utf8ToBase64(text)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function fromBase64Url(value: string): string {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padding = (4 - (base64.length % 4)) % 4;
  return base64ToUtf8(base64 + '==='.slice(0, padding));
}

const COOKIE_NAME = 'rvrentpro_attr';
const COOKIE_PROBE = 'rvrentpro_cd';

// Browsers drop cookies over ~4096 bytes including name and attributes. A
// dropped write fails silently, so stay well under.
const MAX_COOKIE_VALUE = 3500;

/**
 * The cookie rides on every request to the merchant's own servers, so it
 * carries only what attribution needs: ids, UTM, referrer domain and type,
 * landing path, timestamps. Full URLs and the user agent stay in
 * localStorage; device fields are recomputed after a cross-host restore.
 */
const COOKIE_OMITTED_KEYS = [
  'userAgent',
  'referrer',
  'lastTouchReferrer',
  'landingPage',
  'landingPageQuery',
  'lastTouchLandingPage',
  'deviceType',
  'browser',
  'operatingSystem',
  'screenResolution',
];

let cookieDomain: string | null | undefined;

/**
 * Registrable domain (eTLD+1) without shipping the public suffix list: try
 * ever-longer suffixes of the hostname and keep the first the browser
 * accepts. Browsers refuse cookies scoped to a public suffix (`co.uk`), so
 * the probe fails there and succeeds on `merchant.co.uk`. Null means host-only
 * (localhost, IP address, or a browser that rejected every candidate).
 */
function resolveCookieDomain(): string | null {
  if (cookieDomain !== undefined) return cookieDomain;

  cookieDomain = null;
  if (typeof document === 'undefined') return null;

  const host = window.location.hostname;
  const labels = host.split('.');
  if (labels.length < 2 || /^[\d.]+$/.test(host) || host.includes(':')) {
    return null;
  }

  for (let i = labels.length - 2; i >= 0; i--) {
    const candidate = labels.slice(i).join('.');
    document.cookie = `${COOKIE_PROBE}=1; domain=${candidate}; path=/; max-age=10`;
    if (readCookie(COOKIE_PROBE) === '1') {
      document.cookie = `${COOKIE_PROBE}=; domain=${candidate}; path=/; max-age=0`;
      cookieDomain = candidate;
      return candidate;
    }
  }

  return null;
}

function cookieAttributes(maxAgeSeconds: number): string {
  const domain = resolveCookieDomain();
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  const domainAttr = domain ? `; domain=${domain}` : '';
  return `; path=/; max-age=${maxAgeSeconds}; SameSite=Lax${domainAttr}${secure}`;
}

function writeSiteCookie(data: StoredAttributionData): void {
  if (typeof document === 'undefined') return;

  try {
    const attribution: Record<string, unknown> = { ...data.attribution };
    for (let i = 0; i < COOKIE_OMITTED_KEYS.length; i++) {
      delete attribution[COOKIE_OMITTED_KEYS[i]];
    }

    const encoded = toBase64Url(JSON.stringify({ ...data, attribution }));
    if (encoded.length > MAX_COOKIE_VALUE) return;

    const maxAge = EXPIRY_DAYS * 24 * 60 * 60;
    document.cookie = `${COOKIE_NAME}=${encoded}${cookieAttributes(maxAge)}`;
  } catch {
    // Cookies disabled; localStorage still holds the record.
  }
}

function readSiteCookie(): StoredAttributionData | null {
  const raw = readCookie(COOKIE_NAME);
  if (!raw) return null;

  try {
    const data: StoredAttributionData = JSON.parse(fromBase64Url(raw));
    if (!data || !data.attribution || isExpired(data.expiresAt)) return null;
    if (isStalePreSnippetRecord(data)) return null;
    return data;
  } catch {
    return null;
  }
}

function clearSiteCookie(): void {
  if (typeof document === 'undefined') return;

  try {
    document.cookie = `${COOKIE_NAME}=${cookieAttributes(0)}`;
  } catch {
    // Nothing to clear.
  }
}

/**
 * The two copies disagree when the visitor moved between hosts of one site:
 * the cookie followed them, localStorage did not. The older first touch is
 * the real one. Between copies of the same journey the more recently touched
 * wins, localStorage on a tie because the cookie may have shed fields.
 */
function pickStored(
  local: StoredAttributionData | null,
  cookie: StoredAttributionData | null,
): StoredAttributionData | null {
  if (!cookie) return local;
  if (!local) return cookie;

  const localFirst = local.attribution.firstTouchTimestamp || '';
  const cookieFirst = cookie.attribution.firstTouchTimestamp || '';
  if (cookieFirst < localFirst) return cookie;
  if (localFirst < cookieFirst) return local;

  const localLast = local.attribution.lastTouchTimestamp || '';
  const cookieLast = cookie.attribution.lastTouchTimestamp || '';
  return cookieLast > localLast ? cookie : local;
}

/**
 * A cookie copy omits the user agent, device fields and full URLs. Recompute
 * what this browser can tell us; the URLs of the original landing are gone,
 * and the path and domain columns still carry the reporting value.
 */
function rehydrate(data: StoredAttributionData): StoredAttributionData {
  const attribution = data.attribution;

  if (typeof navigator !== 'undefined') {
    attribution.userAgent = attribution.userAgent || navigator.userAgent;
    attribution.deviceType = attribution.deviceType || detectDeviceType();
    attribution.browser = attribution.browser || detectBrowser();
    attribution.operatingSystem = attribution.operatingSystem || detectOS();
  }
  if (typeof window !== 'undefined' && !attribution.screenResolution) {
    attribution.screenResolution = `${window.screen.width}x${window.screen.height}`;
  }

  attribution.referrer = attribution.referrer || '';
  attribution.landingPage = attribution.landingPage || '';
  attribution.landingPageQuery = attribution.landingPageQuery || '';

  return data;
}

/**
 * Get stored attribution data from localStorage
 * Returns null if not found or expired
 */
export function getStoredAttribution(
  options?: StorageOptions,
): TrafficAttribution | null {
  const key = storageKey(options?.scope);
  const local = readLocal(key);

  if (!options?.siteCookie) return local ? local.attribution : null;

  const chosen = pickStored(local, readSiteCookie());
  if (!chosen) return null;

  if (chosen !== local) writeLocal(key, rehydrate(chosen));

  return chosen.attribution;
}

/**
 * Store attribution data in localStorage with expiration
 */
export function storeAttribution(
  data: TrafficAttribution,
  options?: StorageOptions,
): void {
  const stored: StoredAttributionData = {
    attribution: data,
    expiresAt: getExpirationTimestamp(),
    version: SCHEMA_VERSION,
  };

  writeLocal(storageKey(options?.scope), stored);

  if (options?.siteCookie) writeSiteCookie(stored);
}

/**
 * Clear stored attribution data (useful for testing)
 */
export function clearAttribution(options?: StorageOptions): void {
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.removeItem(storageKey(options?.scope));
    } catch (error) {
      console.warn('Failed to clear attribution data:', error);
    }
  }

  if (options?.siteCookie) clearSiteCookie();

  if (typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch (error) {
      console.warn('Failed to clear session data:', error);
    }
  }
}

/**
 * Check if current visit has paid ad click IDs
 * Used to determine if we should update attribution (override first-touch)
 */
function hasPaidAdClick(urlParams: URLSearchParams): boolean {
  if (
    urlParams.get('gclid') ||
    urlParams.get('gbraid') ||
    urlParams.get('wbraid')
  ) {
    return true; // Google Ads
  }

  if (urlParams.get('fbclid')) {
    return true; // Facebook Ads
  }

  if (urlParams.get('msclkid')) {
    return true; // Microsoft/Bing Ads
  }

  return isPaidMedium(normalizeUtmKey(urlParams.get('utm_medium')));
}

export interface CaptureAttributionOptions extends StorageOptions {
  /**
   * Hostnames that must never count as an external referrer, beyond the
   * current host: the merchant's own site and our hosted checkout.
   *
   * Without this, a hop between the two reads as a brand-new referral touch.
   * The damaging case: checkout's fallback capture sees
   * `document.referrer = merchant.com` and records "referral / merchant.com"
   * as the touch, burying the ad click the visitor actually arrived on.
   * Subdomains of a listed host count as internal too.
   */
  internalDomains?: string[];

  /**
   * Recover ad click ids from the ad platforms' own first-party cookies when
   * the click landed on a page our script never ran on (ads point at a
   * homepage, the widget lives elsewhere, so the `?gclid=` is long gone by the
   * time we execute).
   *
   * ONLY safe on the merchant's own domain. Our hosted checkout is shared
   * across every merchant, so a `_gcl_aw` left there by merchant A's earlier
   * checkout would be attributed to merchant B's reservation. Off by default.
   */
  recoverClickIdsFromAdCookies?: boolean;
}

interface AdPlatformClickIds {
  gclid?: string;
  gbraid?: string;
  fbclid?: string;
  msclkid?: string;
}

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;

  // Sandboxed iframes (some site-builder embeds) throw SecurityError on
  // document.cookie. Book Now reads through here; it must never abort.
  try {
    const match = document.cookie.match(
      new RegExp(`(?:^|;\\s*)${name}=([^;]*)`),
    );

    return match ? decodeURIComponent(match[1]) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Ad platforms stamp the click id into a dotted, versioned cookie value:
 * `_gcl_aw` = `GCL.<timestamp>.<gclid>`, `_fbc` = `fb.1.<timestamp>.<fbclid>`.
 * The id itself may be absent; it never contains a dot, but rejoining the tail
 * is the safe read.
 */
function parseDottedClickId(
  value: string | undefined,
  segmentsBeforeId: number,
): string | undefined {
  if (!value) return undefined;

  const parts = value.split('.');
  if (parts.length <= segmentsBeforeId) return undefined;

  return parts.slice(segmentsBeforeId).join('.') || undefined;
}

/**
 * Read click ids from the cookies the merchant's own Google Ads conversion
 * linker / Meta Pixel / Microsoft UET tags wrote. Any advertiser running
 * conversion tracking already has these; we inherit the click for free.
 *
 * These are script-written cookies, so Safari's 7-day cap applies to them
 * exactly as it does to our own storage.
 */
function readAdPlatformClickIds(): AdPlatformClickIds {
  const msclkid = readCookie('_uetmsclkid');

  return {
    gclid: parseDottedClickId(readCookie('_gcl_aw'), 2),
    gbraid: parseDottedClickId(readCookie('_gcl_gb'), 2),
    fbclid: parseDottedClickId(readCookie('_fbc'), 3),
    // UET stores the raw id, occasionally with its own `_uet` prefix.
    msclkid: msclkid ? msclkid.replace(/^_uet/, '') || undefined : undefined,
  };
}

function normalizeHost(host: string): string {
  return host.toLowerCase().replace(/^www\./, '');
}

function isInternalReferrer(
  referrerDomain: string,
  internalDomains: string[],
): boolean {
  const referrer = normalizeHost(referrerDomain);
  if (!referrer) return true;

  return internalDomains.some((domain) => {
    const internal = normalizeHost(domain);
    if (!internal) return false;
    return referrer === internal || referrer.endsWith(`.${internal}`);
  });
}

/**
 * Capture all traffic attribution data
 * Call this when SDK loads or when entering checkout flow
 *
 * Attribution Strategy:
 * - First-touch is immutable until expiry
 * - Last-touch updates whenever the visit carries a signal (paid click id,
 *   UTM params, external referrer); direct revisits leave it untouched
 * - Ad click ids survive a later non-paid touch (see below)
 */
export function captureAttribution(
  options?: CaptureAttributionOptions,
): TrafficAttribution {
  if (typeof window === 'undefined') {
    throw new Error(
      'captureAttribution can only be called in browser environment',
    );
  }

  const urlParams = new URLSearchParams(window.location.search);
  const existingData = getStoredAttribution(options);

  // Source and medium are lowercased here so channel classification and the
  // report dropdowns never split one campaign on a stray capital.
  const utmParams: UTMParams = {
    utm_source: normalizeUtmKey(urlParams.get('utm_source')),
    utm_medium: normalizeUtmKey(urlParams.get('utm_medium')),
    utm_campaign: trimUtmValue(urlParams.get('utm_campaign')),
    utm_term: trimUtmValue(urlParams.get('utm_term')),
    utm_content: trimUtmValue(urlParams.get('utm_content')),
  };

  // The URL is authoritative when present. The ad platforms' cookies recover a
  // click we never saw, because it landed on a page without our script.
  const recovered: AdPlatformClickIds = options?.recoverClickIdsFromAdCookies
    ? readAdPlatformClickIds()
    : {};

  const gclid = urlParams.get('gclid') || recovered.gclid || undefined;
  const gbraid = urlParams.get('gbraid') || recovered.gbraid || undefined;
  const fbclid = urlParams.get('fbclid') || recovered.fbclid || undefined;
  const msclkid = urlParams.get('msclkid') || recovered.msclkid || undefined;
  // No ad-platform cookie carries wbraid.
  const wbraid = urlParams.get('wbraid') || undefined;

  const referrerDomain = extractDomain(document.referrer);

  // A visit only counts as a new "touch" if it carries a signal: paid click
  // id, any UTM param, or an external referrer. Internal navigation and bare
  // direct revisits must not overwrite the stored last touch.
  const internalDomains = [
    window.location.hostname,
    ...(options?.internalDomains ?? []),
  ];
  const hasUtmSignal = Object.values(utmParams).some(Boolean);
  const hasExternalReferrer =
    Boolean(document.referrer) &&
    !isInternalReferrer(referrerDomain, internalDomains);
  const hasTouchSignal =
    hasPaidAdClick(urlParams) || hasUtmSignal || hasExternalReferrer;

  const currentTouch = {
    utmParams,
    gclid,
    gbraid,
    wbraid,
    fbclid,
    msclkid,
    referrer: document.referrer,
    referrerDomain,
    referrerType: classifyReferrer(document.referrer),
    landingPage: window.location.href,
    landingPagePath: window.location.pathname,
    landingPageQuery: window.location.search,
  };

  if (existingData) {
    // First-touch fields are immutable until expiry (AT-2: the old code
    // rebuilt the whole object from the current URL on paid revisits, which
    // falsified first-touch). Revisits with a touch signal update the
    // last-touch block only (AT-1: previously never written).
    existingData.sessionId = getOrCreateSessionId();

    if (hasTouchSignal) {
      existingData.lastTouchUtmParams = utmParams;

      // Ad click ids are sticky: only a NEWER click id of the same platform
      // replaces one. A later non-paid touch (blog referral, email UTM) used
      // to null these out, so a booking that began with a paid click reached
      // the conversion with no gclid and Google Ads could never attribute it.
      // Google Ads' own _gcl_aw cookie has exactly this semantics: a new click
      // overwrites, absence does not.
      existingData.lastTouchGclid = gclid ?? existingData.lastTouchGclid;
      existingData.lastTouchGbraid = gbraid ?? existingData.lastTouchGbraid;
      existingData.lastTouchWbraid = wbraid ?? existingData.lastTouchWbraid;
      existingData.lastTouchFbclid = fbclid ?? existingData.lastTouchFbclid;
      existingData.lastTouchMsclkid = msclkid ?? existingData.lastTouchMsclkid;

      existingData.lastTouchReferrer = currentTouch.referrer;
      existingData.lastTouchReferrerDomain = currentTouch.referrerDomain;
      existingData.lastTouchReferrerType = currentTouch.referrerType;
      existingData.lastTouchLandingPage = currentTouch.landingPage;
      existingData.lastTouchLandingPagePath = currentTouch.landingPagePath;
      existingData.lastTouchTimestamp = new Date().toISOString();
    }

    // Backfill recovered clicks independently of hasTouchSignal: the visit
    // that carried the click landed on a page we never ran on, so by the time
    // we see it this looks like plain internal navigation. Only fills an empty
    // field; a click id we actually observed is never replaced, and
    // first-touch stays immutable (a click found today says nothing about how
    // this visitor first arrived weeks ago).
    existingData.lastTouchGclid =
      existingData.lastTouchGclid ?? recovered.gclid;
    existingData.lastTouchGbraid =
      existingData.lastTouchGbraid ?? recovered.gbraid;
    existingData.lastTouchFbclid =
      existingData.lastTouchFbclid ?? recovered.fbclid;
    existingData.lastTouchMsclkid =
      existingData.lastTouchMsclkid ?? recovered.msclkid;

    storeAttribution(existingData, options);
    return existingData;
  }

  const now = new Date().toISOString();

  // First visit: last-touch mirrors first-touch so the columns are never
  // null for single-touch journeys.
  const attribution: TrafficAttribution = {
    ...currentTouch,
    sessionId: getOrCreateSessionId(),
    firstTouchTimestamp: now,
    lastTouchUtmParams: utmParams,
    lastTouchGclid: gclid,
    lastTouchGbraid: gbraid,
    lastTouchWbraid: wbraid,
    lastTouchFbclid: fbclid,
    lastTouchMsclkid: msclkid,
    lastTouchReferrer: currentTouch.referrer,
    lastTouchReferrerDomain: currentTouch.referrerDomain,
    lastTouchReferrerType: currentTouch.referrerType,
    lastTouchLandingPage: currentTouch.landingPage,
    lastTouchLandingPagePath: currentTouch.landingPagePath,
    lastTouchTimestamp: now,
    deviceType: detectDeviceType(),
    browser: detectBrowser(),
    operatingSystem: detectOS(),
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    userAgent: navigator.userAgent,
  };

  storeAttribution(attribution, options);

  return attribution;
}

/**
 * Does this attribution carry an ad click id on either touch? Used to decide
 * whether a durable copy should win over a freshly captured one; a booking
 * must never lose the click that earned it because storage was evicted.
 */
export function hasAdClickId(attribution: TrafficAttribution | null): boolean {
  if (!attribution) return false;

  return Boolean(
    attribution.gclid ||
    attribution.gbraid ||
    attribution.wbraid ||
    attribution.fbclid ||
    attribution.msclkid ||
    attribution.lastTouchGclid ||
    attribution.lastTouchGbraid ||
    attribution.lastTouchWbraid ||
    attribution.lastTouchFbclid ||
    attribution.lastTouchMsclkid,
  );
}

/**
 * Encode attribution data for URL transfer
 * Used when redirecting from SDK to checkout
 */
export function encodeAttributionForURL(
  attribution: TrafficAttribution,
): string {
  try {
    return utf8ToBase64(JSON.stringify(attribution));
  } catch (error) {
    console.error('Failed to encode attribution data:', error);
    return '';
  }
}

/**
 * Decode attribution data from URL parameter
 * Used in checkout flow to retrieve attribution from SDK
 */
export function decodeAttributionFromURL(
  encoded: string,
): TrafficAttribution | null {
  try {
    const binary = atob(encoded);

    try {
      const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
      return JSON.parse(
        new TextDecoder('utf-8', { fatal: true }).decode(bytes),
      );
    } catch {
      // Legacy fallback: params minted by the pre-UTF-8 encoder while a
      // deploy is in flight (pure Latin-1 JSON).
      return JSON.parse(binary);
    }
  } catch (error) {
    console.error('Failed to decode attribution data:', error);
    return null;
  }
}

/**
 * Database insert format for traffic attribution
 * This matches the TrafficAttributionInsert type from ~/lib/traffic_attribution/types/traffic-attribution
 * Includes both first-touch and last-touch attribution data
 */
export interface FormattedAttributionData {
  // First-touch UTM Parameters
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;

  // First-touch Ad Platform Click IDs
  gclid: string | null;
  gbraid: string | null;
  wbraid: string | null;
  fbclid: string | null;
  msclkid: string | null;

  // First-touch Referrer Data
  referrer: string | null;
  referrer_domain: string | null;
  referrer_type: string | null;

  // First-touch Landing Page Data
  landing_page: string | null;
  landing_page_path: string | null;
  landing_page_query: string | null;

  session_id: string | null;
  first_touch_timestamp: string | null;
  last_touch_timestamp: string;

  device_type: string | null;
  browser: string | null;
  operating_system: string | null;
  screen_resolution: string | null;
  user_agent: string | null;

  // First-touch Traffic Classification
  traffic_type: string;
  channel_grouping: string;

  last_touch_utm_source: string | null;
  last_touch_utm_medium: string | null;
  last_touch_utm_campaign: string | null;
  last_touch_utm_term: string | null;
  last_touch_utm_content: string | null;

  // Last-touch Ad Platform Click IDs
  last_touch_gclid: string | null;
  last_touch_gbraid: string | null;
  last_touch_wbraid: string | null;
  last_touch_fbclid: string | null;
  last_touch_msclkid: string | null;

  last_touch_referrer: string | null;
  last_touch_referrer_domain: string | null;
  last_touch_referrer_type: string | null;

  last_touch_landing_page: string | null;
  last_touch_landing_page_path: string | null;

  last_touch_traffic_type: string | null;
  last_touch_channel_grouping: string | null;
}

/**
 * Convert TrafficAttribution to database insert format
 * Maps camelCase to snake_case for database storage
 * Includes both first-touch and last-touch attribution data
 */
export function formatAttributionForDatabase(
  attribution: TrafficAttribution,
): FormattedAttributionData {
  const lastTouchTrafficType = attribution.lastTouchUtmParams
    ? classifyTrafficTypeFromParams(
        attribution.lastTouchUtmParams,
        attribution.lastTouchReferrerType || 'direct',
        attribution.lastTouchGclid,
        attribution.lastTouchGbraid,
        attribution.lastTouchWbraid,
        attribution.lastTouchFbclid,
        attribution.lastTouchMsclkid,
      )
    : null;

  const lastTouchChannelGrouping = attribution.lastTouchUtmParams
    ? classifyChannelGroupingFromParams(
        attribution.lastTouchUtmParams,
        attribution.lastTouchReferrerType || 'direct',
        attribution.lastTouchGclid,
        attribution.lastTouchGbraid,
        attribution.lastTouchWbraid,
        attribution.lastTouchFbclid,
        attribution.lastTouchMsclkid,
      )
    : null;

  return {
    // First-touch UTM Parameters
    utm_source: attribution.utmParams.utm_source || null,
    utm_medium: attribution.utmParams.utm_medium || null,
    utm_campaign: attribution.utmParams.utm_campaign || null,
    utm_term: attribution.utmParams.utm_term || null,
    utm_content: attribution.utmParams.utm_content || null,

    // First-touch Ad Platform Click IDs
    gclid: attribution.gclid || null,
    gbraid: attribution.gbraid || null,
    wbraid: attribution.wbraid || null,
    fbclid: attribution.fbclid || null,
    msclkid: attribution.msclkid || null,

    // First-touch Referrer Data
    referrer: attribution.referrer || null,
    referrer_domain: attribution.referrerDomain || null,
    referrer_type: attribution.referrerType || null,

    // First-touch Landing Page Data
    landing_page: attribution.landingPage || null,
    landing_page_path: attribution.landingPagePath || null,
    landing_page_query: attribution.landingPageQuery || null,

    session_id: attribution.sessionId || null,
    first_touch_timestamp: attribution.firstTouchTimestamp || null,
    last_touch_timestamp:
      attribution.lastTouchTimestamp || new Date().toISOString(),

    device_type: attribution.deviceType || null,
    browser: attribution.browser || null,
    operating_system: attribution.operatingSystem || null,
    screen_resolution: attribution.screenResolution || null,
    user_agent: attribution.userAgent || null,

    // First-touch Traffic Classification
    traffic_type: classifyTrafficType(attribution),
    channel_grouping: classifyChannelGrouping(attribution),

    last_touch_utm_source: attribution.lastTouchUtmParams?.utm_source || null,
    last_touch_utm_medium: attribution.lastTouchUtmParams?.utm_medium || null,
    last_touch_utm_campaign:
      attribution.lastTouchUtmParams?.utm_campaign || null,
    last_touch_utm_term: attribution.lastTouchUtmParams?.utm_term || null,
    last_touch_utm_content: attribution.lastTouchUtmParams?.utm_content || null,

    // Last-touch Ad Platform Click IDs
    last_touch_gclid: attribution.lastTouchGclid || null,
    last_touch_gbraid: attribution.lastTouchGbraid || null,
    last_touch_wbraid: attribution.lastTouchWbraid || null,
    last_touch_fbclid: attribution.lastTouchFbclid || null,
    last_touch_msclkid: attribution.lastTouchMsclkid || null,

    last_touch_referrer: attribution.lastTouchReferrer || null,
    last_touch_referrer_domain: attribution.lastTouchReferrerDomain || null,
    last_touch_referrer_type: attribution.lastTouchReferrerType || null,

    last_touch_landing_page: attribution.lastTouchLandingPage || null,
    last_touch_landing_page_path: attribution.lastTouchLandingPagePath || null,

    last_touch_traffic_type: lastTouchTrafficType,
    last_touch_channel_grouping: lastTouchChannelGrouping,
  };
}

/**
 * Traffic type and channel grouping share one rule: an explicit UTM medium
 * says how the link was sent and wins; the referrer only guesses. Medium
 * tests are GA4's, so `paid_social`, `Paid`, `e-mail` all land where GA4
 * would put them instead of falling through to Direct.
 */
function classifyTrafficTypeFromParams(
  utmParams: UTMParams,
  referrerType: string,
  gclid?: string,
  gbraid?: string,
  wbraid?: string,
  fbclid?: string,
  msclkid?: string,
): string {
  const medium = normalizeUtmKey(utmParams.utm_medium);
  const source = normalizeUtmKey(utmParams.utm_source);

  // Click ids first: the ad platform's own stamp beats any tag.
  if (gclid || gbraid || wbraid) return 'paid_search';
  if (fbclid) return 'paid_social';
  if (msclkid) return 'paid_search';

  if (isDisplayMedium(medium)) return 'paid';

  if (isPaidMedium(medium)) {
    if (isSocialSource(source) || referrerType === 'social_media') {
      return 'paid_social';
    }
    if (isSearchSource(source) || referrerType === 'search_engine') {
      return 'paid_search';
    }
    return 'paid';
  }

  if (isEmailMedium(medium)) return 'email';
  if (isSocialMedium(medium)) return 'social';
  if (medium === 'organic') return 'organic';
  if (medium === 'referral' || medium === 'affiliate') return 'referral';

  // A tagged link with a medium we do not recognise was still a link the
  // visitor clicked, so it is not Direct.
  if (medium && (referrerType === 'direct' || referrerType === 'internal')) {
    return 'other';
  }

  if (referrerType === 'email') return 'email';
  if (referrerType === 'social_media') return 'social';
  if (referrerType === 'video') return 'video';
  if (referrerType === 'community') return 'community';
  if (referrerType === 'news') return 'news';
  if (referrerType === 'blog') return 'blog';
  if (referrerType === 'search_engine') return 'organic';
  if (referrerType === 'referral') return 'referral';
  if (referrerType === 'direct' || referrerType === 'internal') return 'direct';

  return 'other';
}

/**
 * Channel grouping (Google Analytics standard). Paid traffic that is neither
 * social nor search files under Paid Search rather than a Paid Other bucket:
 * for a rental fleet that case is rare and a new channel costs a colour and a
 * legend slot in every report.
 */
function classifyChannelGroupingFromParams(
  utmParams: UTMParams,
  referrerType: string,
  gclid?: string,
  gbraid?: string,
  wbraid?: string,
  fbclid?: string,
  msclkid?: string,
): string {
  const medium = normalizeUtmKey(utmParams.utm_medium);
  const source = normalizeUtmKey(utmParams.utm_source);

  if (gclid || gbraid || wbraid || msclkid) return 'Paid Search';
  if (fbclid) return 'Paid Social';

  // Display before paid: `cpm` matches both and is a display buy.
  if (isDisplayMedium(medium)) return 'Display';

  if (isPaidMedium(medium)) {
    if (isSocialSource(source) || referrerType === 'social_media') {
      return 'Paid Social';
    }
    return 'Paid Search';
  }

  if (medium === 'organic') return 'Organic Search';
  if (isSocialMedium(medium)) return 'Organic Social';
  if (isEmailMedium(medium)) return 'Email';
  if (medium === 'referral' || medium === 'affiliate') return 'Referral';

  if (medium && (referrerType === 'direct' || referrerType === 'internal')) {
    return 'Other';
  }

  if (referrerType === 'search_engine') return 'Organic Search';
  if (referrerType === 'social_media') return 'Organic Social';
  if (referrerType === 'email') return 'Email';
  if (referrerType === 'video') return 'Organic Video';
  if (
    referrerType === 'referral' ||
    referrerType === 'community' ||
    referrerType === 'news' ||
    referrerType === 'blog'
  ) {
    return 'Referral';
  }
  if (referrerType === 'direct' || referrerType === 'internal') return 'Direct';

  return 'Other';
}

export function classifyTrafficType(attribution: TrafficAttribution): string {
  return classifyTrafficTypeFromParams(
    attribution.utmParams,
    attribution.referrerType,
    attribution.gclid,
    attribution.gbraid,
    attribution.wbraid,
    attribution.fbclid,
    attribution.msclkid,
  );
}

export function classifyChannelGrouping(
  attribution: TrafficAttribution,
): string {
  return classifyChannelGroupingFromParams(
    attribution.utmParams,
    attribution.referrerType,
    attribution.gclid,
    attribution.gbraid,
    attribution.wbraid,
    attribution.fbclid,
    attribution.msclkid,
  );
}
